<template>
    <div class="research-literature">
        <div class="filter-bar">
            <div class="filter-item">
                <span class="filter-label">分类</span>
                <el-select size="small" v-model="filters.category">
                    <el-option
                        v-for="item in categoryOptions"
                        :key="item"
                        :label="item"
                        :value="item"
                        :disabled="!availableCategories.has(item)"
                    />
                </el-select>
            </div>
            <div class="filter-actions">
                <el-button size="small" type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                <el-button size="small" type="danger" plain icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
            </div>
        </div>

        <el-table
            class="literature-table"
            :data="pageList"
            :header-cell-style="headerCellStyle"
            :empty-text="emptyText"
            stripe
            size="small"
        >
            <el-table-column
                type="index"
                label="序号"
                width="70"
                align="center"
                :index="indexMethod"
            />
            <el-table-column prop="title" label="标题" min-width="220">
                <template slot-scope="scope">
                    <overflow-text :text="scope.row.title || ''" />
                </template>
            </el-table-column>
            <el-table-column prop="abstract" label="摘要" min-width="320">
                <template slot-scope="scope">
                    <overflow-text :text="scope.row.abstract || ''" />
                </template>
            </el-table-column>
            <el-table-column prop="type" label="分类" width="120" align="center" />
        </el-table>

        <el-pagination
            class="page-wrap"
            background
            layout="total, prev, pager, next, jumper"
            :current-page="pageInfo.pageNum"
            :page-size="pageInfo.pageSize"
            :total="pageInfo.total"
            @current-change="changePage"
        />
    </div>
</template>

<script>
import OverflowText from './overflow_text.vue';
import { RESEARCH_LITERATURE_CATEGORIES } from '../researchData';

// 分类「有数据」的口径与 filteredList 保持一致：分类列展示值（type，缺失时已回退为所属分类）
// 与数组归属分类任一命中，即视为该分类有数据。
function collectAvailableCategories(list) {
    const available = new Set();
    (Array.isArray(list) ? list : []).forEach((item) => {
        if (!item) return;
        if (item.category) available.add(item.category);
        if (item.type) available.add(item.type);
    });
    return available;
}

// 检索分类无「全部」选项：默认选中第一个有数据的分类，7 类全空时回退为第一个分类。
function resolveDefaultCategory(available) {
    return RESEARCH_LITERATURE_CATEGORIES.find((item) => available.has(item)) || RESEARCH_LITERATURE_CATEGORIES[0];
}

export default {
    name: 'ResearchLiterature',
    components: { OverflowText },
    props: {
        allList: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        // 默认选中第一个有数据的分类，避免首屏落在无数据的分类上。
        const category = resolveDefaultCategory(collectAvailableCategories(this.allList));
        return {
            // 检索分类固定为 7 类，与接口返回的 7 个文献数组字段一一对应。
            categoryOptions: RESEARCH_LITERATURE_CATEGORIES,
            filters: { category },
            appliedFilters: { category },
            pageInfo: {
                pageNum: 1,
                pageSize: 10,
                total: 0,
            },
            headerCellStyle: {
                background: '#F5F7FA',
                color: '#303133',
                fontWeight: 'bold',
            },
        };
    },
    computed: {
        // 有数据的分类，用于下拉置灰：无数据的分类不可选。
        availableCategories() {
            return collectAvailableCategories(this.allList);
        },
        filteredList() {
            const { category } = this.appliedFilters;
            return this.allList.filter((item) => {
                // 分类列展示 type（缺失时已回退为所属分类），两项任一命中即保留。
                if (category && item.category !== category && item.type !== category) return false;
                return true;
            });
        },
        // 表格空状态文案：整体无数据 vs 当前分类无文献。
        emptyText() {
            return this.allList.length ? '该分类暂无文献' : '暂无文献数据';
        },
        pageList() {
            const { pageNum, pageSize } = this.pageInfo;
            const start = (pageNum - 1) * pageSize;
            return this.filteredList.slice(start, start + pageSize);
        },
    },
    watch: {
        allList() {
            this.handleReset();
        },
        filteredList: {
            immediate: true,
            handler(list) {
                this.pageInfo.total = list.length;
                const maxPage = Math.max(1, Math.ceil(list.length / this.pageInfo.pageSize) || 1);
                if (this.pageInfo.pageNum > maxPage) {
                    this.pageInfo.pageNum = maxPage;
                }
            },
        },
    },
    methods: {
        indexMethod(index) {
            return (this.pageInfo.pageNum - 1) * this.pageInfo.pageSize + index + 1;
        },
        handleSearch() {
            this.appliedFilters = { ...this.filters };
            this.pageInfo.pageNum = 1;
        },
        handleReset() {
            const category = resolveDefaultCategory(this.availableCategories);
            this.filters = { category };
            this.appliedFilters = { category };
            this.pageInfo.pageNum = 1;
        },
        changePage(page) {
            this.pageInfo.pageNum = page;
        },
    },
};
</script>

<style lang="less" scoped>
.research-literature {
    .filter-bar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px 16px;
        margin-bottom: 16px;
    }

    .filter-item {
        display: flex;
        align-items: center;

        .filter-label {
            margin-right: 8px;
            font-size: 14px;
            color: #303133;
            white-space: nowrap;
        }

        .el-select {
            width: 140px;
        }
    }

    .filter-actions {
        display: flex;
        align-items: center;
        gap: 8px;

        .el-button--primary {
            background: #165DFF;
            border-color: #165DFF;
        }
    }

    .literature-table {
        width: 100%;

        ::v-deep .el-button--text {
            color: #165DFF;
            padding: 0 4px;
        }
    }

    .page-wrap {
        margin-top: 16px;
        text-align: right;
    }
}
</style>
